import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  ShieldCheck, RefreshCw, Check, X, Filter, FileText,
} from "lucide-react";
import api from "../utils/api";
import { Layout } from "../components/layout";
import { useAuth } from "../context/AuthContext";

const STATUSES = ["pending", "approved", "rejected"];

export default function QCCAAdmin() {
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await api.get("/qcca/applications", { params });
      setItems(res.data.applications || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isAdmin]);

  const review = async (status) => {
    if (!selected) return;
    try {
      const res = await api.patch(`/qcca/applications/${selected.id}/review`, {
        status,
        admin_notes: notes,
      });
      toast.success(`Application ${status}.`);
      setItems((arr) => arr.map((a) => (a.id === selected.id ? res.data.application : a)));
      setSelected(res.data.application);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed.");
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center text-gray-400 font-mono text-sm">
          Please log in to continue.
        </div>
      </Layout>
    );
  }
  if (!isAdmin) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center text-gray-400 font-mono text-sm">
          QCCA verification desk is admin-only.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">QCCA · Verification Desk</div>
              <h1 className="mt-3 font-headline text-4xl text-white leading-tight">Review <span className="italic text-[#C5A52A]">board applications.</span></h1>
            </div>
            <button onClick={load}
              className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              data-testid="qcca-admin-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          <div className="flex items-center gap-2 mb-6">
            <Filter className="w-4 h-4 text-[#C5A52A]" />
            {["all", ...STATUSES].map((s) => (
              <button key={s} onClick={() => setFilter(s)}
                className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border transition-colors ${
                  filter === s ? "bg-[#C5A52A] text-black border-[#C5A52A]" : "text-gray-400 border-[#1a1a1a] hover:border-[#C5A52A]/50 hover:text-white"
                }`}
                data-testid={`qcca-admin-filter-${s}`}>
                {s}
              </button>
            ))}
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
              {items.length} result{items.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 border border-[#1a1a1a] bg-[#0a0a0a] divide-y divide-[#1a1a1a] max-h-[75vh] overflow-y-auto">
              {items.length === 0 && !loading && (
                <div className="p-10 text-center text-gray-500 font-mono text-sm">
                  No applications {filter !== "all" ? `with status "${filter}"` : "yet"}.
                </div>
              )}
              {items.map((a) => (
                <button key={a.id} onClick={() => { setSelected(a); setNotes(a.admin_notes || ""); }}
                  className={`w-full text-left p-5 hover:bg-black transition-colors ${
                    selected?.id === a.id ? "bg-black border-l-2 border-[#C5A52A]" : ""
                  }`}
                  data-testid={`qcca-admin-row-${a.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-headline text-lg text-white leading-tight">{a.full_name}</div>
                    <StatusBadge status={a.status} />
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-gray-500">{a.condo_corporation_name}</div>
                  <div className="mt-2 font-mono text-[10px] text-gray-600 uppercase tracking-[0.18em]">
                    {new Date(a.created_at).toLocaleString()} · {a.board_position.replace(/_/g, " ")}
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-7 border border-[#1a1a1a] bg-[#0a0a0a] p-8 min-h-[40vh]">
              {!selected ? (
                <div className="h-full flex items-center justify-center text-gray-500 font-mono text-sm">
                  Select an application to review.
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-headline text-3xl text-white leading-tight">{selected.full_name}</h2>
                      <div className="mt-1 font-mono text-[11px] text-gray-500">
                        {selected.email}{selected.phone ? ` · ${selected.phone}` : ""}
                      </div>
                    </div>
                    <StatusBadge status={selected.status} large />
                  </div>

                  <dl className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-5 text-sm">
                    <Info label="Condo corporation" value={selected.condo_corporation_name} />
                    <Info label="Board position" value={selected.board_position.replace(/_/g, " ")} />
                    <Info label="Building address" value={selected.building_address} />
                    <Info label="Years on board" value={selected.years_on_board ?? "—"} />
                    <Info label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
                    <Info label="Reviewed" value={selected.reviewed_at ? `${new Date(selected.reviewed_at).toLocaleString()}` : "—"} />
                  </dl>

                  {selected.message && (
                    <div className="mt-6">
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Applicant message</div>
                      <p className="text-gray-300 leading-relaxed bg-black border border-[#1a1a1a] p-4">{selected.message}</p>
                    </div>
                  )}

                  {selected.agm_document_signed_url && (
                    <a href={selected.agm_document_signed_url} target="_blank" rel="noreferrer"
                      className="mt-6 inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2.5 transition-colors"
                      data-testid="qcca-admin-agm-link">
                      <FileText className="w-3.5 h-3.5" /> View AGM document (15-min signed link)
                    </a>
                  )}

                  <div className="mt-8">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Admin notes</div>
                    <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-4 py-3 outline-none transition-colors resize-none"
                      placeholder="Verifier notes (admins only)" data-testid="qcca-admin-notes-input" />
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button onClick={() => review("approved")} disabled={selected.status === "approved"}
                      className="inline-flex items-center gap-2 bg-[#C5A52A] disabled:bg-[#1a1a1a] disabled:text-gray-500 hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
                      data-testid="qcca-admin-approve-btn">
                      <Check className="w-4 h-4" /> Approve &amp; issue badge
                    </button>
                    <button onClick={() => review("rejected")} disabled={selected.status === "rejected"}
                      className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-red-500 disabled:border-[#1a1a1a] disabled:text-gray-500 text-red-400 hover:text-red-300 font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
                      data-testid="qcca-admin-reject-btn">
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">{label}</dt>
      <dd className="mt-1 text-white text-sm capitalize">{String(value)}</dd>
    </div>
  );
}

function StatusBadge({ status, large }) {
  const map = {
    pending: "border-[#C5A52A]/50 text-[#C5A52A]",
    approved: "border-green-500/50 text-green-400",
    rejected: "border-red-500/50 text-red-400",
  };
  const size = large ? "px-3 py-1.5 text-[11px]" : "px-2 py-1 text-[10px]";
  return (
    <span className={`border ${map[status] || "border-[#1a1a1a] text-gray-400"} font-mono ${size} uppercase tracking-[0.22em]`}>
      {status}
    </span>
  );
}
